import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, X, BookOpen, FileText, ShieldCheck } from "lucide-react";
import { useEffect } from "react";
// landing-analytics is dynamically imported to keep Supabase out of the landing bundle
import { Reveal } from "@/components/Reveal";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { useAuth } from "@/hooks/use-auth";

export const Route = createFileRoute("/home-v4")({
  head: () => ({
    meta: [
      { title: "Honest essay & dissertation feedback for students — Tiger's Eye" },
      {
        name: "description",
        content:
          "Upload your university essay or dissertation draft and get an impartial academic review against your brief, level and referencing style. Critique only — never ghostwriting.",
      },
      { property: "og:url", content: "https://tigerseye.app/" },
      {
        property: "og:title",
        content: "Honest essay & dissertation feedback for students — Tiger's Eye",
      },
      {
        property: "og:description",
        content:
          "Upload your university essay or dissertation draft and get an impartial academic review against your brief, level and referencing style. Critique only — never ghostwriting.",
      },
    ],
  }),
  component: HomeV4,
});

// "Library / journal" palette — warm off-white, deep forest ink, brass accent
const BG = "var(--paper)";
const SURFACE = "var(--card)";
const INK = "var(--primary)";
const INK_SOFT = "var(--ink-secondary)";
const INK_FAINT = "var(--ink-tertiary)";
const BRASS = "var(--editorial-accent, var(--accent))";
const RULE = "var(--rule)";
const ALERT = "var(--destructive)";

const SERIF =
  '"Iowan Old Style", "Source Serif Pro", "Source Serif 4", "Charter", Georgia, ui-serif, serif';

function HomeV4() {
  const { user } = useAuth();
  useEffect(() => {
    void import("@/lib/landing-analytics").then((m) => {
      void m.trackLandingEvent("page_view", "home-v4");
    });
  }, []);
  const onCta = (location: string) =>
    void import("@/lib/landing-analytics").then((m) =>
      m.trackLandingEvent("cta_click", "home-v4", { location }),
    );

  return (
    <main className="min-h-screen w-full" style={{ background: BG, color: INK }}>
      <style>{`
        .hv4-serif { font-family: ${SERIF}; font-feature-settings: "liga","kern"; }
        .hv4-eyebrow {
          font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
          font-weight: 600; color: ${BRASS};
        }
        .hv4-rule { border-top: 1px solid ${RULE}; }
        .hv4-cta {
          transition: transform .18s ease, box-shadow .18s ease, background .18s ease;
        }
        .hv4-cta-primary { background: ${INK}; color: ${BG}; }
        .hv4-cta-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 22px -14px ${INK}; background: color-mix(in oklch, ${INK} 85%, black); }
        .hv4-cta-ghost { color: ${INK}; border: 1px solid ${RULE}; }
        .hv4-cta-ghost:hover { background: ${SURFACE}; }
        .hv4-chip {
          display: inline-flex; align-items: center; padding: 6px 12px;
          border: 1px solid ${RULE}; border-radius: 999px; font-size: 13px; color: ${INK_SOFT};
          background: ${SURFACE};
        }
        @keyframes hv4-fade-up {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hv4-reveal { opacity: 0; animation: hv4-fade-up .7s ease-out forwards; }
        .hv4-cta svg { transition: transform .25s cubic-bezier(.22,1,.36,1); }
        .hv4-cta:hover svg { transform: translateX(3px); }
        .hv4-table { border-collapse: collapse; width: 100%; table-layout: fixed; font-variant-numeric: tabular-nums; }
        .hv4-table th, .hv4-table td {
          padding: 14px 12px; text-align: center; border-bottom: 1px solid ${RULE}; font-size: 14px;
        }
        .hv4-table th:first-child, .hv4-table td:first-child { text-align: left; color: ${INK}; }
        .hv4-table thead th { color: ${INK_SOFT}; font-weight: 500; }
        .hv4-table thead th.hv4-us { color: ${INK}; font-weight: 700; }
        .hv4-table tbody tr { transition: background .2s ease; }
        .hv4-table tbody tr:hover { background: ${BG}; }
        .hv4-faq summary {
          cursor: pointer; padding: 18px 0; list-style: none; display: flex;
          justify-content: space-between; align-items: center; font-weight: 500; color: ${INK};
          transition: color .2s ease;
        }
        .hv4-faq summary:hover { color: ${BRASS}; }
        .hv4-faq summary::-webkit-details-marker { display: none; }
        .hv4-faq[open] summary .hv4-faq-icon { transform: rotate(45deg); }
        .hv4-faq-icon {
          flex-shrink: 0; width: 14px; height: 14px; position: relative;
          transition: transform .25s cubic-bezier(.22,1,.36,1);
        }
        .hv4-faq-icon::before, .hv4-faq-icon::after {
          content: ""; position: absolute; background: ${INK}; top: 50%; left: 50%;
        }
        .hv4-faq-icon::before { width: 14px; height: 1.5px; transform: translate(-50%, -50%); }
        .hv4-faq-icon::after  { width: 1.5px; height: 14px; transform: translate(-50%, -50%); }
        
        .hv4-faq p { padding: 0 0 18px; color: ${INK_SOFT}; font-size: 15px; line-height: 1.65; }

        /* Micro-interactions */
        .hv4-feature { transition: transform .35s cubic-bezier(.22,1,.36,1), box-shadow .35s ease; }
        .hv4-feature:hover { transform: translateY(-3px); box-shadow: 0 18px 30px -22px ${INK}; }
        .hv4-feature svg { transition: transform .35s cubic-bezier(.22,1,.36,1); }
        .hv4-feature:hover svg { transform: scale(1.12) rotate(-4deg); }
        .hv4-step { transition: transform .35s cubic-bezier(.22,1,.36,1), border-color .25s ease; }
        .hv4-step:hover { transform: translateY(-3px); border-color: ${BRASS}; }
        .hv4-chip { transition: background .2s ease, border-color .2s ease, color .2s ease; }
        .hv4-chip:hover { background: ${INK}; color: ${BG}; border-color: ${INK}; }
        @keyframes hv4-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .hv4-float { animation: hv4-float 6s ease-in-out infinite; }
        @keyframes hv4-dot-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: .7; }
        }
        .hv4-dot { animation: hv4-dot-pulse 2.6s ease-in-out infinite; }
        .hv4-dot-2 { animation-delay: .6s; }
        .hv4-dot-3 { animation-delay: 1.2s; }
        @media (prefers-reduced-motion: reduce) {
          .hv4-float, .hv4-dot { animation: none !important; }
        }
      `}</style>

      {/* Sticky slim nav */}
      <header
        className="sticky top-0 z-20"
        style={{ background: BG, borderBottom: `1px solid ${RULE}` }}
      >
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="hv4-serif text-lg font-semibold" style={{ color: INK }}>
            Tiger&apos;s Eye
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/pricing" className="text-sm" style={{ color: INK_SOFT }}>
              Pricing
            </Link>
            {user ? (
              <Link to="/dashboard" className="text-sm" style={{ color: INK_SOFT }}>
                Dashboard
              </Link>
            ) : (
              <Link to="/login" className="text-sm" style={{ color: INK_SOFT }}>
                Sign in
              </Link>
            )}
            <Link
              to={user ? "/reviews/new" : "/reviews/new"}
              onClick={() => onCta("nav")}
              className="hv4-cta hv4-cta-primary inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium"
            >
              Review my draft
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

        </div>
      </header>

      {/* Hero: asymmetric 2-col */}
      <section className="mx-auto max-w-6xl px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="hv4-reveal lg:col-span-7">
            <p className="hv4-eyebrow mb-5">FOR SCHOOL, COLLEGE, AND UNIVERSITY STUDENTS · UK &amp; GLOBAL</p>
            <h1
              className="hv4-serif text-balance text-4xl leading-[1.05] tracking-tight sm:text-5xl md:text-6xl"
              style={{ color: INK }}
            >
              Honest essay &amp; dissertation feedback —{" "}
              <span style={{ color: BRASS }}>before you hit submit.</span>
            </h1>
            <p
              className="mt-6 max-w-xl font-sans text-[1.05rem] leading-[1.6]"
              style={{ color: INK_SOFT }}
            >
              Tiger&apos;s Eye reviews your university draft against your brief, your level
              of study, and your referencing style. It critiques structure, argument, evidence
              and citations — and it never writes a single word for you.
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Link
                to="/reviews/new"
                onClick={() => onCta("hero")}
                className="hv4-cta hv4-cta-primary inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base font-medium"
              >
                Start a free review
                <ArrowRight className="h-4 w-4" />
              </Link>
              <a
                href="#how"
                className="hv4-cta hv4-cta-ghost inline-flex items-center justify-center rounded-full px-6 py-3.5 text-base font-medium"
              >
                See how it works
              </a>
            </div>

            <p className="mt-6 text-xs" style={{ color: INK_FAINT }}>
              Harvard · APA · MHRA · OSCOLA · Chicago · Vancouver · IEEE
            </p>
          </div>

          {/* Right: stylised draft card with margin notes */}
          <div className="hv4-reveal lg:col-span-5" style={{ animationDelay: ".15s" }}>
            <div
              className="hv4-float relative rounded-lg p-6 shadow-sm"
              style={{ background: SURFACE, border: `1px solid ${RULE}` }}
            >
              <div className="mb-3 flex items-center gap-2 text-xs" style={{ color: INK_FAINT }}>
                <FileText className="h-3.5 w-3.5" />
                Dissertation_draft_v3.docx
              </div>
              <div className="hv4-serif space-y-2.5" style={{ color: INK }}>
                <p className="text-[15px] leading-relaxed">
                  The relationship between welfare reform and food insecurity has been
                  <span
                    style={{
                      background: "var(--editorial-accent-soft, var(--accent))",
                      borderBottom: `1.5px solid ${BRASS}`,
                      padding: "0 2px",
                    }}
                  >
                    {" "}widely debated in recent literature{" "}
                  </span>
                  (Smith, 2019).
                </p>
                <p className="text-[15px] leading-relaxed" style={{ color: INK_SOFT }}>
                  This dissertation argues that the 2012 reforms had a measurable effect on…
                </p>
              </div>

              <div
                className="mt-5 space-y-3 rounded-md p-4"
                style={{ background: BG, border: `1px solid ${RULE}` }}
              >
                <div className="flex gap-2 text-[13px]">
                  <span
                    className="hv4-dot mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ background: BRASS }}
                  />
                  <p style={{ color: INK }}>
                    <strong>Unsupported claim.</strong>{" "}
                    <span style={{ color: INK_SOFT }}>
                      &ldquo;Widely debated&rdquo; needs a second source or a hedge.
                    </span>
                  </p>
                </div>
                <div className="flex gap-2 text-[13px]">
                  <span
                    className="hv4-dot hv4-dot-2 mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ background: ALERT }}
                  />
                  <p style={{ color: INK }}>
                    <strong>Referencing.</strong>{" "}
                    <span style={{ color: INK_SOFT }}>
                      Harvard requires page number for direct paraphrase.
                    </span>
                  </p>
                </div>
                <div className="flex gap-2 text-[13px]">
                  <span
                    className="hv4-dot hv4-dot-3 mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ background: INK }}
                  />
                  <p style={{ color: INK }}>
                    <strong>Socratic question.</strong>{" "}
                    <span style={{ color: INK_SOFT }}>
                      What counter-evidence does your reader expect here?
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="mx-auto max-w-6xl px-6 pb-12">
        <p className="hv4-eyebrow mb-4">Built for</p>
        <div className="flex flex-wrap gap-2">
          {[
            "Undergraduates",
            "Master's students",
            "PhD candidates",
            "International students",
            "Mature returners",
            "Law & humanities",
            "STEM dissertations",
          ].map((c) => (
            <span key={c} className="hv4-chip">
              {c}
            </span>
          ))}
        </div>
      </section>

      {/* What you get */}
      <section id="how" className="mx-auto max-w-6xl px-6 py-16">
        <div className="hv4-rule mb-12" />
        <p className="hv4-eyebrow mb-3">What you get in 60 seconds</p>
        <h2
          className="hv4-serif max-w-3xl text-balance text-3xl leading-tight sm:text-4xl"
          style={{ color: INK }}
        >
          A pro-level second read on your essay, dissertation or assignment.
        </h2>

        <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-lg sm:grid-cols-3"
          style={{ background: RULE, border: `1px solid ${RULE}` }}
        >
          {[
            {
              icon: BookOpen,
              t: "Structure & argument review",
              b: "We read for thesis clarity, sequence, evidence and how well the draft answers the assignment brief.",
            },
            {
              icon: ShieldCheck,
              t: "Referencing & integrity audit",
              b: "Citations checked against Harvard, APA, MHRA, OSCOLA, Chicago and Vancouver. Implausible or missing references flagged.",
            },
            {
              icon: FileText,
              t: "Socratic questions",
              b: "Tough, marker-style questions to consider before you revise — so your voice gets sharper, not replaced.",
            },
          ].map(({ icon: Icon, t, b }, i) => (
            <Reveal
              key={t}
              delay={i * 90}
              y={20}
              className="hv4-feature p-6"
              style={{ background: SURFACE }}
            >
              <Icon className="h-5 w-5" style={{ color: BRASS }} strokeWidth={1.5} />
              <h3 className="hv4-serif mt-4 text-lg" style={{ color: INK }}>
                {t}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: INK_SOFT }}>
                {b}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="hv4-rule mb-12" />
        <p className="hv4-eyebrow mb-3">Why students pick Tiger&apos;s Eye</p>
        <h2
          className="hv4-serif max-w-3xl text-balance text-3xl leading-tight sm:text-4xl"
          style={{ color: INK }}
        >
          Not an essay mill. Not a grammar checker. Not ChatGPT.
        </h2>
        <p className="mt-3 max-w-2xl text-base" style={{ color: INK_SOFT }}>
          A clear-eyed look at how Tiger&apos;s Eye compares with the tools students
          usually reach for the night before submission.
        </p>

        <Reveal y={24}>
          <div
            className="mt-8 overflow-x-auto rounded-lg"
            style={{ background: SURFACE, border: `1px solid ${RULE}` }}
          >
            <table className="hv4-table">
              <colgroup>
                <col style={{ width: "32%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "17%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th className="hv4-us">Tiger&apos;s Eye</th>
                  <th>Essay mills</th>
                  <th>Generic AI writers</th>
                  <th>Grammar checkers</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Refuses to write your essay", true, false, false, true],
                  ["Reviews against your assignment brief", true, false, false, false],
                  ["Checks Harvard / APA / OSCOLA references", true, false, false, false],
                  ["Flags academic integrity risks", true, false, false, false],
                  ["Asks Socratic, marker-style questions", true, false, false, false],
                  ["Predicts your grade", false, false, false, false],
                  ["Uses your draft to train AI", false, "?", "?", "?"],
                ].map(([label, ...cells]) => (
                  <tr key={label as string}>
                    <td>{label as string}</td>
                    {cells.map((v, i) => (
                      <td key={i}>
                        {v === true ? (
                          <Check
                            className="mx-auto h-4 w-4"
                            style={{ color: i === 0 ? "#3f7a4d" : INK }}
                            strokeWidth={2.5}
                          />
                        ) : v === false ? (
                          <X
                            className="mx-auto h-4 w-4"
                            style={{ color: "#a8443a" }}
                            strokeWidth={2}
                          />
                        ) : (
                          <span style={{ color: INK_FAINT }}>?</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      </section>

      {/* How a review works */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="hv4-rule mb-12" />
        <p className="hv4-eyebrow mb-3">How a review works</p>
        <ol className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
          {[
            {
              n: "01",
              t: "Upload your draft",
              b: "PDF, DOCX or paste. Tell us your brief, level and referencing style.",
            },
            {
              n: "02",
              t: "Choose a review mode",
              b: "Quick Integrity Check, Structural Review, or Full Academic Critique.",
            },
            {
              n: "03",
              t: "Read, reflect, revise",
              b: "Get critique and Socratic questions. The revision stays yours.",
            },
          ].map((s, i) => (
            <Reveal
              key={s.n}
              as="li"
              delay={i * 90}
              y={20}
              className="hv4-step rounded-lg p-6"
              style={{
                background: SURFACE,
                border: `1px solid ${RULE}`,
              }}
            >
              <div className="hv4-serif text-sm" style={{ color: BRASS }}>
                {s.n}
              </div>
              <h3 className="hv4-serif mt-2 text-lg" style={{ color: INK }}>
                {s.t}
              </h3>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: INK_SOFT }}>
                {s.b}
              </p>
            </Reveal>
          ))}
        </ol>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-6 pb-16">
        <div className="hv4-rule mb-12" />
        <p className="hv4-eyebrow mb-3">Honest answers</p>
        <h2
          className="hv4-serif text-balance text-3xl leading-tight sm:text-4xl"
          style={{ color: INK }}
        >
          Frequently asked questions
        </h2>

        <div className="mt-8" style={{ borderTop: `1px solid ${RULE}` }}>
          {[
            {
              q: "Is using Tiger's Eye allowed by my university?",
              a: "Tiger's Eye gives critique only — the same kind of feedback a tutor, study buddy or writing centre would. It never writes or rewrites assessed text. Most universities permit this kind of feedback, but you remain responsible for following your institution's rules on AI use and acknowledgement.",
            },
            {
              q: "Will it write or rewrite my essay?",
              a: "No. We refuse to produce model answers, rewrite assessed passages, paraphrase to bypass plagiarism tools, or invent sources. The words you submit stay your words.",
            },
            {
              q: "Does it predict my grade?",
              a: "No. We will not predict, promise or even hint at a likely mark. We tell you what is strong, what is weak, and what a careful marker may reasonably ask.",
            },
            {
              q: "Which referencing styles does it check?",
              a: "Harvard, APA, MHRA, OSCOLA, Chicago and Vancouver. We flag missing page numbers, inconsistent formatting and implausible or possibly fabricated references.",
            },
            {
              q: "Is my draft used to train AI models?",
              a: "No. Your draft is processed to give you the review and is not used as training data.",
            },
            {
              q: "How is this different from Grammarly or ChatGPT?",
              a: "Grammarly fixes surface mechanics. ChatGPT will happily write your essay for you. Tiger's Eye does neither — it reads your draft as a critical reader would, and helps you see it with fresh eyes before you submit.",
            },
          ].map((f) => (
            <details
              key={f.q}
              className="hv4-faq"
              style={{ borderBottom: `1px solid ${RULE}` }}
            >
              <summary>
                <span className="hv4-serif text-lg">{f.q}</span>
                <span className="hv4-faq-icon" aria-hidden />
              </summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Free-place callout */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        <div className="hv4-rule mb-12" />
        <Reveal y={20}>
          <div
            className="flex flex-col gap-5 rounded-lg px-6 py-8 sm:flex-row sm:items-center sm:justify-between"
            style={{ background: SURFACE, border: `1px solid ${RULE}` }}
          >
            <div>
              <p className="hv4-eyebrow mb-2">Free for the first 49 students</p>
              <p className="hv4-serif text-xl" style={{ color: INK }}>
                Founding places are limited — after they fill, it's &pound;4.95 per term.
              </p>
            </div>
            <Link
              to="/pricing"
              onClick={() => onCta("free-place-callout")}
              className="hv4-cta hv4-cta-primary inline-flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium"
            >
              See pricing
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* Final CTA */}
      <section className="px-6 pb-24">
        <div
          className="mx-auto max-w-5xl rounded-2xl px-8 py-14 text-center sm:px-12 sm:py-20"
          style={{ background: INK, color: BG }}
        >
          <p
            className="hv4-eyebrow mb-4"
            style={{ color: BRASS }}
          >
            Before you click submit
          </p>
          <h2
            className="hv4-serif mx-auto max-w-2xl text-balance text-3xl leading-tight sm:text-4xl"
          >
            Get fresh eyes on your draft. Keep your voice. Keep your grade yours.
          </h2>
          <div className="mt-8">
            <Link
              to="/reviews/new"
              onClick={() => onCta("footer")}
              className="hv4-cta inline-flex items-center gap-2 rounded-full px-7 py-4 text-base font-medium"
              style={{ background: BG, color: INK }}
            >
              Start a free review
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-16">
        <NewsletterSignup source="home" />
      </section>

      <footer
        className="mx-auto max-w-6xl px-6 pb-10 flex items-center justify-between text-xs"
        style={{ color: INK_FAINT }}
      >
        <span>© {new Date().getFullYear()} Tiger&apos;s Eye · by PRAXIA</span>
        <span className="flex items-center gap-4">
          <Link to="/pricing" style={{ color: INK_SOFT }}>
            Pricing
          </Link>
          <Link to="/terms" style={{ color: INK_SOFT }}>
            Terms &amp; ethos
          </Link>
        </span>
      </footer>
    </main>
  );
}
